import { Button } from '@/common/ui/button'
import {
	Dialog,
	DialogContent,
	DialogHeader,
	DialogTitle,
	DialogTrigger,
} from '@/common/ui/dialog'
import {
	Form,
	FormControl,
	FormField,
	FormItem,
	FormMessage,
} from '@/common/ui/form'
import { Input } from '@/common/ui/input'
import useMutationUpdateGameTypeProvider from '@/modules/game/api/hooks/useMutationUpdateGameTypeProvider'
import { GameQueryKeys } from '@/modules/game/keys'
import { GameProviderListTableData } from '@/modules/game/type'
import { zodResolver } from '@hookform/resolvers/zod'
import { useQueryClient } from '@tanstack/react-query'
import { Edit } from 'lucide-react'
import { useState } from 'react'
import { useForm } from 'react-hook-form'
import { toast } from 'sonner'
import showErrorToast from '@/common/utils/showErrorToast'
import * as z from 'zod'

const formSchema = z.object({
	order: z
		.number({ invalid_type_error: 'กรุณากรอกลำดับ' })
		.min(1, 'ลำดับต้องมากกว่า 0'),
})

interface EditOrderGameProviderProps {
	row: GameProviderListTableData
}

export default function EditOrderGameProvider({
	row,
}: EditOrderGameProviderProps) {
	const [open, setOpen] = useState(false)
	const { mutate: updateGameTypeProvider } = useMutationUpdateGameTypeProvider()
	const queryClient = useQueryClient()

	const form = useForm<z.infer<typeof formSchema>>({
		resolver: zodResolver(formSchema),
		defaultValues: {
			order: row.order,
		},
	})

	const onSubmit = (values: z.infer<typeof formSchema>) => {
		updateGameTypeProvider(
			{
				id: row.id,
				payload: {
					order: values.order,
				},
			},
			{
				onSuccess: () => {
					toast.success('อัพเดทลำดับสำเร็จ')
					queryClient.invalidateQueries({
						queryKey: [GameQueryKeys.GetGameProviderList],
					})
					setOpen(false)
				},
				onError: (error) => {
					showErrorToast(error.message || 'เกิดข้อผิดพลาด')
				},
			},
		)
	}

	return (
		<Dialog open={open} onOpenChange={setOpen}>
			<DialogTrigger asChild>
				<Button variant="ghost" size="icon">
					<Edit className="h-4 w-4" />
				</Button>
			</DialogTrigger>
			<DialogContent className="sm:max-w-[425px]">
				<DialogHeader>
					<DialogTitle>แก้ไขลำดับ</DialogTitle>
				</DialogHeader>
				<Form {...form}>
					<form
						onSubmit={form.handleSubmit(onSubmit)}
						className="grid gap-4 py-4"
					>
						<FormField
							control={form.control}
							name="order"
							render={({ field }) => (
								<FormItem>
									<FormControl>
										<Input
											type="number"
											placeholder="ลำดับ"
											{...field}
											onChange={(e) =>
												field.onChange(Number(e.target.value) || null)
											}
										/>
									</FormControl>
									<FormMessage />
								</FormItem>
							)}
						/>
						<Button type="submit" disabled={form.formState.isSubmitting}>
							บันทึก
						</Button>
					</form>
				</Form>
			</DialogContent>
		</Dialog>
	)
}
